/**
 * `Contas da equipe` (platform design §5.2.1; Figma `638:18821`, card 5): a team's direct
 * accounts, attached and detached one at a time through the existing per-account `PUT
 * .../tracked-accounts/{id}/team`. Every write lands immediately -- there is no submit to batch
 * them behind, so this is a plain `Dialog`, not `FormDialog`, and its one footer button only
 * closes it.
 *
 * A candidate already in this team is excluded from search by its own `team_id`, which is also
 * what tells a candidate in a different team apart from one with none (`em <equipe>` vs.
 * `sem equipe`).
 */
import { Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { DialogSurfaceFooter } from '../../../chrome/form';
import { EntityRow } from '../../../chrome/entity/EntitySwitcher';
import { IconHint } from '../../../chrome/hint';
import { ErrorState } from '../../../chrome/state/ErrorState';
import { st } from '../../../chrome/state/messages';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { Spinner } from '../../../ui/spinner';
import { useCan } from '../../session/orgContext';
import { accountOption } from '../accounts/NoSelection';
import { t, plural } from '../i18n';
import { useMoveAccount, useStructure, useTeamAccounts, useTeamAccountSearch } from './hooks';
import type { TeamOut, TeamTrackedAccountRow } from './schemas';

function accountLabel(account: TeamTrackedAccountRow): string {
  return account.label ?? account.handle ?? account.id;
}

export interface TeamAccountsProps {
  campaignId: string;
  team: TeamOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamAccounts(props: TeamAccountsProps): ReactNode {
  const { campaignId, team, open, onOpenChange } = props;
  const canWrite = useCan('control_plane.write');
  const structureQuery = useStructure(campaignId);
  const accountsQuery = useTeamAccounts(campaignId, team.id, open);
  const [query, setQuery] = useState('');
  const searchQuery = useTeamAccountSearch(campaignId, query, open);
  const moveMutation = useMoveAccount(campaignId);
  const [failedId, setFailedId] = useState<string | null>(null);

  if (!canWrite) return null;

  const teamNameById = new Map(
    (structureQuery.data?.teams ?? []).map((candidate) => [candidate.id, candidate.name]),
  );
  const accounts = accountsQuery.data ?? [];
  const trimmedQuery = query.trim();
  const results = (searchQuery.data ?? []).filter((account) => account.team_id !== team.id);
  const locale = getLocale();

  function attach(account: TeamTrackedAccountRow): void {
    setFailedId(null);
    moveMutation.mutate(
      { trackedAccountId: account.id, team_id: team.id },
      { onError: () => setFailedId(account.id) },
    );
  }

  function detach(account: TeamTrackedAccountRow): void {
    setFailedId(null);
    moveMutation.mutate(
      { trackedAccountId: account.id, team_id: null },
      { onError: () => setFailedId(account.id) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{t('teams.accountsDialog.title', { name: team.name })}</DialogTitle>
            <Chip size="sm">{team.level_name}</Chip>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              className="pl-8"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('teams.accountsDialog.searchPlaceholder')}
              aria-label={t('teams.accountsDialog.searchPlaceholder')}
            />
          </div>

          {trimmedQuery !== '' && (
            <div className="flex flex-col overflow-hidden rounded-md border border-input">
              {searchQuery.isPending && (
                <div className="flex items-center gap-2 px-3 py-2 text-caption text-muted-foreground">
                  <Spinner aria-label={st('loading')} />
                  {st('loading')}
                </div>
              )}
              {searchQuery.isError && (
                <div className="p-3">
                  <ErrorState failure={searchQuery.error.failure} onRetry={searchQuery.refetch} />
                </div>
              )}
              {searchQuery.isSuccess && results.length === 0 && (
                <p className="px-3 py-2 text-caption text-muted-foreground">
                  {t('teams.accountsDialog.searchEmpty')}
                </p>
              )}
              {results.map((account) => {
                // A name missing from `teamNameById` (the structure query racing an edit to
                // the team tree) still means "elsewhere", never "sem equipe" -- fall back to
                // a generic label rather than mislabelling an assigned account as unassigned.
                const elsewhere =
                  account.team_id !== null
                    ? (teamNameById.get(account.team_id) ??
                      t('teams.accountsDialog.elsewhereUnknown'))
                    : null;
                return (
                  <div key={account.id} className="flex flex-col">
                    <button
                      type="button"
                      className="flex items-center gap-2 px-3 py-2 text-left text-body-sm hover:bg-secondary"
                      onClick={() => attach(account)}
                    >
                      <EntityRow
                        option={accountOption(account)}
                        trailing={
                          <span className="text-caption text-muted-foreground">
                            {elsewhere !== null
                              ? t('teams.accountsDialog.elsewhere', { team: elsewhere })
                              : t('teams.accountsDialog.unassigned')}
                          </span>
                        }
                      />
                    </button>
                    {failedId === account.id && (
                      <p role="alert" className="px-3 pb-2 text-caption text-destructive">
                        {t('teams.accountsDialog.moveFailed')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <p className="text-caption font-medium text-muted-foreground">
              {plural('teams.accountsDialog.count.other', accounts.length, {
                count: integer(accounts.length, locale),
              })}
            </p>

            {accountsQuery.isPending && (
              <div className="flex items-center gap-2 py-2 text-caption text-muted-foreground">
                <Spinner aria-label={st('loading')} />
                {st('loading')}
              </div>
            )}
            {accountsQuery.isError && (
              <ErrorState failure={accountsQuery.error.failure} onRetry={accountsQuery.refetch} />
            )}
            {accountsQuery.isSuccess && accounts.length === 0 && (
              <p className="py-2 text-caption text-muted-foreground">
                {t('teams.accountsDialog.empty')}
              </p>
            )}
            {accounts.map((account) => (
              <div key={account.id} className="flex flex-col gap-1 py-1.5">
                <div className="flex items-center gap-2 text-body-sm">
                  <EntityRow
                    option={accountOption(account)}
                    trailing={
                      <IconHint
                        label={t('teams.accountsDialog.remove', { name: accountLabel(account) })}
                      >
                        <button type="button" onClick={() => detach(account)}>
                          <X aria-hidden className="size-3.5 text-muted-foreground" />
                        </button>
                      </IconHint>
                    }
                  />
                </div>
                {failedId === account.id && (
                  <p role="alert" className="text-caption text-destructive">
                    {t('teams.accountsDialog.moveFailed')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogSurfaceFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('teams.accountsDialog.done')}
          </Button>
        </DialogSurfaceFooter>
      </DialogContent>
    </Dialog>
  );
}
